import React, { useCallback, useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { trackRead, trackReads } from '../../services/firebaseUsageTracker';

// Firestore pricing (as of 2024)
const PRICING = {
  writes: 0.18 / 100000,
  reads: 0.06 / 100000,
  deletes: 0.02 / 100000,
};

type UserUsage = {
  uid: string;
  email?: string;
  sessionReads: number;
  sessionWrites: number;
  sessionDeletes: number;
  sessionStart: number;
  lastActive?: { seconds: number };
};

type AggregatedStats = {
  totalReads: number;
  totalWrites: number;
  totalDeletes: number;
  activeUsers: number;
  userBreakdown: UserUsage[];
  lastRefreshed: number;
};

type Props = {
  expanded?: boolean;
};

export const AggregatedUsageTracker: React.FC<Props> = ({ expanded = true }) => {
  const [stats, setStats] = useState<AggregatedStats>({
    totalReads: 0,
    totalWrites: 0,
    totalDeletes: 0,
    activeUsers: 0,
    userBreakdown: [],
    lastRefreshed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(expanded);

  // One-time fetch of all usage data (not real-time)
  const loadStats = useCallback(async () => {
    setRefreshing(true);
    
    try {
      // One-time read of all users
      const usersSnap = await getDocs(query(collection(db, 'users')));
      trackReads('users', usersSnap.docs.length, 'usage-tracker-users');
      
      // Build email map
      const emails = new Map<string, string>();
      usersSnap.docs.forEach(userDoc => {
        const data = userDoc.data();
        if (data.email) {
          emails.set(userDoc.id, data.email);
        }
      });
      
      // One-time read of each user's usage doc
      const usagePromises = usersSnap.docs.map(async (userDoc) => {
        try {
          const usageRef = doc(db, 'users', userDoc.id, 'state', 'usage');
          const usageSnap = await getDoc(usageRef);
          trackRead('users/state/usage', 'usage-tracker-state');
          
          if (usageSnap.exists()) {
            const data = usageSnap.data();
            return {
              uid: userDoc.id,
              email: emails.get(userDoc.id),
              sessionReads: data.sessionReads || 0,
              sessionWrites: data.sessionWrites || 0,
              sessionDeletes: data.sessionDeletes || 0,
              sessionStart: data.sessionStart || Date.now(),
              lastActive: data.lastActive,
            } as UserUsage;
          }
        } catch {
          // User might not have usage doc yet
        }
        return null;
      });
      
      const usageResults = (await Promise.all(usagePromises)).filter((u): u is UserUsage => u !== null);
      
      // Aggregate stats
      let totalReads = 0;
      let totalWrites = 0;
      let totalDeletes = 0;
      const activeThreshold = Date.now() - 30 * 60 * 1000; // 30 min
      let activeUsers = 0;
      
      usageResults.forEach(usage => {
        totalReads += usage.sessionReads;
        totalWrites += usage.sessionWrites;
        totalDeletes += usage.sessionDeletes;
        if (usage.lastActive && usage.lastActive.seconds * 1000 > activeThreshold) {
          activeUsers++;
        }
      });
      
      setStats({
        totalReads,
        totalWrites,
        totalDeletes,
        activeUsers,
        userBreakdown: usageResults
          .filter(u => u.sessionReads > 0 || u.sessionWrites > 0)
          .sort((a, b) => (b.sessionReads + b.sessionWrites) - (a.sessionReads + a.sessionWrites)),
        lastRefreshed: Date.now(),
      });
    } catch (err) {
      console.warn('Failed to load usage stats:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Load once on mount
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const formatCost = (cost: number) => {
    if (cost < 0.0001) return '$0.0000';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  const formatNumber = (n: number) => n.toLocaleString();

  const totalCost = 
    stats.totalReads * PRICING.reads +
    stats.totalWrites * PRICING.writes +
    stats.totalDeletes * PRICING.deletes;

  const formatTimeAgo = (timestamp?: { seconds: number } | number) => {
    if (!timestamp) return 'Never';
    const ms = typeof timestamp === 'number' ? timestamp : timestamp.seconds * 1000;
    const now = Date.now();
    const diff = now - ms;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">📊</span>
          <h3 className="text-lg font-semibold">Aggregated Firebase Usage</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-emerald-900/50 text-emerald-300 text-xs rounded">
            {stats.activeUsers} active now
          </span>
          <button
            onClick={loadStats}
            disabled={refreshing}
            className="text-xs px-2 py-1 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-50"
          >
            {refreshing ? '...' : '↻ Refresh'}
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {/* Last refreshed indicator */}
      {stats.lastRefreshed > 0 && (
        <div className="text-xs text-gray-500 mb-2">
          Last updated: {formatTimeAgo(stats.lastRefreshed)}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="bg-gray-900/60 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Total Reads</div>
          <div className="text-lg font-bold text-blue-400">{formatNumber(stats.totalReads)}</div>
        </div>
        <div className="bg-gray-900/60 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Total Writes</div>
          <div className="text-lg font-bold text-emerald-400">{formatNumber(stats.totalWrites)}</div>
        </div>
        <div className="bg-gray-900/60 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Total Deletes</div>
          <div className="text-lg font-bold text-red-400">{formatNumber(stats.totalDeletes)}</div>
        </div>
        <div className="bg-gray-900/60 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Est. Cost</div>
          <div className="text-lg font-bold text-yellow-400">{formatCost(totalCost)}</div>
        </div>
      </div>

      {/* Free Tier Status */}
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-300 mb-2">Free Tier Usage (Today - All Users)</h4>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-400">Writes ({formatNumber(stats.totalWrites)} / 20,000)</span>
              <span className="text-gray-400">{((stats.totalWrites / 20000) * 100).toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  stats.totalWrites / 20000 > 0.8 ? 'bg-red-500' : 
                  stats.totalWrites / 20000 > 0.5 ? 'bg-yellow-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(100, (stats.totalWrites / 20000) * 100)}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-400">Reads ({formatNumber(stats.totalReads)} / 50,000)</span>
              <span className="text-gray-400">{((stats.totalReads / 50000) * 100).toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  stats.totalReads / 50000 > 0.8 ? 'bg-red-500' : 
                  stats.totalReads / 50000 > 0.5 ? 'bg-yellow-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(100, (stats.totalReads / 50000) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {isExpanded && stats.userBreakdown.length > 0 && (
        <div className="border-t border-gray-700 pt-3 mt-3">
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Usage by User</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {stats.userBreakdown.map(user => (
              <div key={user.uid} className="flex items-center justify-between text-xs bg-gray-900/40 rounded px-2 py-1.5">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-gray-300 truncate">
                    {user.email || user.uid.slice(0, 8) + '...'}
                  </span>
                  <span className="text-gray-500">{formatTimeAgo(user.lastActive)}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <span className="text-blue-400">{user.sessionReads}R</span>
                  <span className="text-emerald-400">{user.sessionWrites}W</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 text-xs text-gray-500">
        Note: This aggregates client-side tracked operations across all users. 
        Click Refresh to update. Check Firebase Console for accurate billing data.
      </div>
    </div>
  );
};
